import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Home from "./components/Home";
import Login from "./components/Login";
import Navbar from "./components/Navbar";
import About from "./components/About";
import Contact from "./components/Contact";
import Courses from "./components/Courses";
import Nursing from "./components/Nursing";
import Paramedical from "./components/Paramedical";
import NotFound from "./components/NotFound";
import StudentPage from "./pages/StudentPage";
import StaffPage from "./pages/StaffPage";
import AdminPage from "./pages/AdminPage";
import StaffList from "./components/StaffList";
import StaffProfile from "./components/StaffProfile";
import StudentProfile from "./components/StudentProfile";
import ManageStudentHome from "./pages/ManageStudentHome";
import ManageFeesHome from "./pages/ManageFeesHome";
import FeeListPage from "./pages/FeeList";
import FeeAccountTabs from "./components/FeeAccountTabs";
import BulkFeeUpload from "./pages/BulkFeeUpload";
import FeeReports from "./pages/FeeReports";
import ScholarshipManager from "./pages/ScholarshipManager";
import FeePayment from "./components/FeePayment";
import IncomeExpenseManager from "./components/IncomeExpenseManager";
import ManageIncome from "./components/ManageIncome"; // <-- ADDED import
import ReportsTab from "./components/ReportsTab";
import Settings from "./pages/Settings";
import EnrollStudents from "./pages/EnrollStudents";
import AttendanceManager from "./pages/AttendanceManager";
import IdCardGenerator from "./pages/IdCardGenerator";
import BulkStudentUpload from "./pages/BulkStudentUpload";
import StudentReports from "./pages/StudentReports";
import RotationPlanEditor from "./pages/Rotation/RotationPlanEditor";
import BatchManagement from "./components/BatchManagement";
import StudentListTabs from "./components/StudentListTabs";
import PrincipalPage from "./pages/PrincipalPage";
import ApplyFormRouter from "./components/ApplyFormRouter";
import Applicationformlist from "./components/Applicationformlist";
import AdminResetPassword from "./pages/AdminResetPassword";
import ForgotPassword from "./pages/ForgotPassword";
import ManageExpense from "./components/ManageExpense";
import SalaryManagement from "./components/SalaryManagement";
import API_URL from "./apiConfig";

function App() {
  const [user, setUser] = useState(() => {
    const savedUser = sessionStorage.getItem("user");
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [token, setToken] = useState(() => sessionStorage.getItem("token") || "");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.onbeforeunload = () => {
      sessionStorage.removeItem("user");
      sessionStorage.removeItem("token");
    };
    return () => {
      window.onbeforeunload = null;
    };
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      if (token && user) {
        try {
          const response = await fetch(`${API_URL}/api/auth/check-auth`, {
            method: "GET",
            headers: {
              "Authorization": `Bearer ${token}`
            }
          });
          if (response.ok) {
            setLoading(false);
          } else {
            handleLogout();
            setLoading(false);
          }
        } catch (err) {
          handleLogout();
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };
    checkAuth();
    // eslint-disable-next-line
  }, [token, user]);

  useEffect(() => {
    if (!user) {
      setToken("");
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("user");
    }
  }, [user]);

  const handleLogin = (loginUser, loginToken) => {
    setUser(loginUser);
    setToken(loginToken);
    sessionStorage.setItem("user", JSON.stringify(loginUser));
    sessionStorage.setItem("token", loginToken);
  };

  const handleLogout = () => {
    setUser(null);
    setToken("");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("user");
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Router>
      <Navbar user={user} onLogout={handleLogout} />
      <Routes>
        {/* Public/info routes */}
        <Route path="/" element={<Home user={user} />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/courses" element={<Courses />} />
        <Route path="/courses/nursing" element={<Nursing />} />
        <Route path="/courses/paramedical" element={<Paramedical />} />
        <Route path="/apply" element={<ApplyFormRouter />} />
        <Route path="/admin-reset-password" element={<AdminResetPassword />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />

        {/* Auth routes */}
        <Route
          path="/login"
          element={
            user ? (
              user.role === "student" ? (
                <Navigate to="/student" replace />
              ) : user.role === "staff" ? (
                <Navigate to="/staff" replace />
              ) : user.role === "admin" ? (
                <Navigate to="/admin" replace />
              ) : user.role === "principal" ? (
                <Navigate to="/principal" replace />
              ) : (
                <div>Unknown role</div>
              )
            ) : (
              <Login setUser={handleLogin} setToken={setToken} />
            )
          }
        />

        {/* Student routes */}
        <Route
          path="/student/*"
          element={
            user && user.role === "student" ? (
              <StudentPage user={user} token={token} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* Staff routes */}
        <Route
          path="/staff/*"
          element={
            user && user.role === "staff" ? (
              <StaffPage user={user} token={token} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* Principal dashboard route */}
        <Route
          path="/principal/*"
          element={
            user && user.role === "principal" ? (
              <PrincipalPage user={user} token={token} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* Staff Rotational Plan */}
        <Route
          path="/staff/rotation-plan"
          element={
            user && user.role === "staff" ? (
              <RotationPlanEditor />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* Admin dashboard (with nested routes) */}
        <Route
          path="/admin/*"
          element={
            user && user.role === "admin" ? (
              <AdminPage user={user} token={token} onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        >
          <Route path="manage-staff" element={<StaffList token={token} />} />
          <Route path="manage-staff/:id" element={<StaffProfile token={token} />} />
          <Route path="manage-student/*" element={<ManageStudentHome />}>
            <Route path="list" element={<StudentListTabs token={token} />} />
            <Route path="enroll" element={<EnrollStudents />} />
            <Route path="attendance" element={<AttendanceManager />} />
            <Route path="idcard" element={<IdCardGenerator />} />
            <Route path="bulk" element={<BulkStudentUpload />} />
            <Route path="reports" element={<StudentReports />} />
          </Route>
          <Route path="manage-student/:id" element={<StudentProfile token={token} />} />
          <Route path="manage-fees/*" element={<ManageFeesHome />}>
            <Route path="list" element={<FeeListPage token={token} />} />
            <Route path="list/:studentId" element={<FeeAccountTabs token={token} user={user} />} />
            <Route path="bulk" element={<BulkFeeUpload token={token} />} />
            <Route path="reports" element={<FeeReports token={token} />} />
            <Route path="scholarship" element={<ScholarshipManager token={token} />} />
          </Route>
          <Route path="fee-payment/:id" element={<FeePayment token={token} />} />

          {/* ADDED: ManageIncome route */}
          <Route path="manage-income" element={<ManageIncome token={token} user={user} />} />

          {/* existing income-expense (if you still need the aggregated manager) */}
          <Route path="income-expense" element={<IncomeExpenseManager token={token} />} />

          <Route path="manage-expense" element={<ManageExpense token={token} user={user} />} />
          <Route path="manage-salary" element={<SalaryManagement token={token} user={user} />} />
          <Route path="reports" element={<ReportsTab token={token} />} />
          <Route path="settings" element={<Settings token={token} user={user} onLogout={handleLogout} />} />
          <Route path="annual-fee-setup" element={<BatchManagement token={token} user={user} />} />
          <Route path="applications" element={<Applicationformlist user={user} />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
  );
}

export default App;